import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format, parseISO, isBefore, isAfter } from 'date-fns';
import { 
  Plus, Target, Calendar, CheckCircle2, Circle, 
  Edit2, Trash2, User, TrendingUp, Award, Lock
} from 'lucide-react';

export const GoalsPage = () => {
  const { token } = useAuth();
  const { canAddEdit, requireSubscription } = useSubscription();
  const [goals, setGoals] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [filterRider, setFilterRider] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const emptyGoal = {
    rider_id: '',
    goal_text: '',
    target_date: '',
    status: 'in_progress'
  };
  
  const [newGoal, setNewGoal] = useState(emptyGoal);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    const headers = getHeaders();
    try {
      const [goalsRes, ridersRes] = await Promise.all([
        fetch(`${API}/goals`, { headers }),
        fetch(`${API}/rider-profiles`, { headers })
      ]);

      if (goalsRes.ok) setGoals(await goalsRes.json());
      if (ridersRes.ok) setRiders(await ridersRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!newGoal.rider_id || !newGoal.goal_text) {
      toast.error('Please select a rider and enter a goal');
      return;
    }

    try {
      const response = await fetch(`${API}/goals`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(newGoal)
      });

      if (response.ok) {
        const goal = await response.json();
        setGoals([goal, ...goals]);
        setDialogOpen(false);
        setNewGoal(emptyGoal);
        toast.success('Goal added!');
      }
    } catch (error) {
      toast.error('Failed to add goal');
    }
  };

  const handleUpdateGoal = async (e) => {
    e.preventDefault();
    if (!editingGoal) return;

    try {
      const response = await fetch(`${API}/goals/${editingGoal.goal_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          rider_id: editingGoal.rider_id,
          goal_text: editingGoal.goal_text,
          target_date: editingGoal.target_date || null,
          status: editingGoal.status
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setGoals(goals.map(g => g.goal_id === updated.goal_id ? updated : g));
        setEditDialogOpen(false);
        setEditingGoal(null);
        toast.success('Goal updated!');
      }
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const handleToggleStatus = async (goal) => {
    const newStatus = goal.status === 'achieved' ? 'in_progress' : 'achieved';
    try {
      const response = await fetch(`${API}/goals/${goal.goal_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const updated = await response.json();
        setGoals(goals.map(g => g.goal_id === updated.goal_id ? updated : g));
        toast.success(newStatus === 'achieved' ? 'Goal achieved!' : 'Goal reopened');
      }
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      const response = await fetch(`${API}/goals/${goalId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        setGoals(goals.filter(g => g.goal_id !== goalId));
        toast.success('Goal deleted');
      }
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const getRiderName = (riderId) => {
    const rider = riders.find(r => r.rider_id === riderId);
    return rider?.name || 'Unknown Rider';
  };

  const isOverdue = (targetDate) => {
    if (!targetDate) return false;
    return isBefore(parseISO(targetDate), new Date());
  };

  const filteredGoals = goals.filter(goal => {
    const matchesRider = filterRider === 'all' || goal.rider_id === filterRider;
    const matchesStatus = filterStatus === 'all' || goal.status === filterStatus;
    return matchesRider && matchesStatus;
  });

  const inProgressCount = goals.filter(g => g.status === 'in_progress').length;
  const achievedCount = goals.filter(g => g.status === 'achieved').length;

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="h-10 w-32 skeleton rounded-lg" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-40 skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="goals-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Goals</h1>
            <p className="text-muted-foreground mt-1">
              {inProgressCount} in progress, {achievedCount} achieved
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-forest hover:bg-forest-dark gap-2 disabled:opacity-50" 
                data-testid="add-goal-btn"
                onClick={(e) => {
                  if (!canAddEdit()) {
                    e.preventDefault();
                    requireSubscription();
                  }
                }}
                disabled={!canAddEdit()}
              >
                {!canAddEdit() && <Lock className="w-4 h-4" />}
                <Plus className="w-4 h-4" />
                Add Goal
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-serif">Add New Goal</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddGoal} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Rider *</Label>
                  <Select value={newGoal.rider_id} onValueChange={(v) => setNewGoal({ ...newGoal, rider_id: v })}>
                    <SelectTrigger data-testid="goal-rider-select">
                      <SelectValue placeholder="Select rider" />
                    </SelectTrigger>
                    <SelectContent>
                      {riders.map(rider => (
                        <SelectItem key={rider.rider_id} value={rider.rider_id}>
                          {rider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {riders.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No riders found. <Link to="/rider-profiles" className="text-forest hover:underline">Add a rider first</Link>
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Goal *</Label>
                  <Textarea 
                    value={newGoal.goal_text} 
                    onChange={(e) => setNewGoal({ ...newGoal, goal_text: e.target.value })}
                    placeholder="e.g., Complete first dressage competition, Master flying lead changes..."
                    rows={3}
                    required
                    data-testid="goal-text-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Target Date</Label>
                  <Input 
                    type="date" 
                    value={newGoal.target_date} 
                    onChange={(e) => setNewGoal({ ...newGoal, target_date: e.target.value })}
                    data-testid="goal-target-date-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-forest hover:bg-forest-dark"
                  disabled={riders.length === 0}
                  data-testid="submit-goal-btn"
                >
                  Add Goal
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-forest/10 rounded-lg">
                <Target className="w-5 h-5 text-forest" />
              </div>
              <div>
                <p className="text-2xl font-bold">{goals.length}</p>
                <p className="text-sm text-muted-foreground">Total Goals</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inProgressCount}</p>
                <p className="text-sm text-muted-foreground">In Progress</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Award className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{achievedCount}</p>
                <p className="text-sm text-muted-foreground">Achieved</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{riders.length}</p>
                <p className="text-sm text-muted-foreground">Riders</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={filterRider} onValueChange={setFilterRider}>
            <SelectTrigger className="w-full sm:w-48 bg-white" data-testid="filter-goals-rider">
              <SelectValue placeholder="Filter by rider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Riders</SelectItem>
              {riders.map(rider => (
                <SelectItem key={rider.rider_id} value={rider.rider_id}>
                  {rider.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-48 bg-white" data-testid="filter-goals-status">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="achieved">Achieved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Goals List */}
        {goals.length === 0 ? (
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="py-16 text-center">
              <Target className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
              <h3 className="text-lg font-semibold mb-2">No Goals Yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-4">
                Set goals for your riders to track progress and celebrate achievements.
              </p>
              {riders.length === 0 ? (
                <Link to="/rider-profiles">
                  <Button variant="outline">Add a Rider First</Button>
                </Link>
              ) : (
                <Button onClick={() => setDialogOpen(true)} className="bg-forest hover:bg-forest-dark">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Goal
                </Button>
              )}
            </CardContent>
          </Card>
        ) : filteredGoals.length === 0 ? (
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No goals match current filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredGoals.map((goal, i) => (
              <Card 
                key={goal.goal_id} 
                className={`border-0 shadow-card bg-white card-hover animate-slideUp ${goal.status === 'achieved' ? 'ring-2 ring-emerald-200' : ''}`}
                style={{ animationDelay: `${i * 0.05}s` }}
                data-testid={`goal-card-${goal.goal_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <button 
                      onClick={() => handleToggleStatus(goal)}
                      className="mt-1 flex-shrink-0 transition-transform hover:scale-110"
                      data-testid={`toggle-goal-${goal.goal_id}`}
                    >
                      {goal.status === 'achieved' ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                      ) : (
                        <Circle className="w-6 h-6 text-muted-foreground" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${goal.status === 'achieved' ? 'line-through text-muted-foreground' : ''}`}>
                        {goal.goal_text}
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                        <User className="w-3.5 h-3.5" />
                        <span>{getRiderName(goal.rider_id)}</span>
                      </div>
                      {goal.target_date && (
                        <div className="flex items-center gap-2 mt-1 text-sm">
                          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className={goal.status !== 'achieved' && isOverdue(goal.target_date) ? 'text-destructive font-medium' : 'text-muted-foreground'}>
                            Target: {format(parseISO(goal.target_date), 'MMM d, yyyy')}
                          </span>
                          {goal.status !== 'achieved' && isOverdue(goal.target_date) && (
                            <Badge variant="destructive" className="text-xs">Overdue</Badge>
                          )}
                        </div>
                      )}
                      {goal.achieved_date && (
                        <div className="flex items-center gap-2 mt-1 text-sm text-emerald-600">
                          <Award className="w-3.5 h-3.5" />
                          <span>Achieved: {format(parseISO(goal.achieved_date), 'MMM d, yyyy')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setEditingGoal(goal); setEditDialogOpen(true); }}
                      data-testid={`edit-goal-${goal.goal_id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive"
                          data-testid={`delete-goal-${goal.goal_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Goal?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this goal.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteGoal(goal.goal_id)}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingGoal(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">Edit Goal</DialogTitle>
            </DialogHeader>
            {editingGoal && (
              <form onSubmit={handleUpdateGoal} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Rider *</Label>
                  <Select value={editingGoal.rider_id} onValueChange={(v) => setEditingGoal({ ...editingGoal, rider_id: v })}>
                    <SelectTrigger data-testid="edit-goal-rider-select">
                      <SelectValue placeholder="Select rider" />
                    </SelectTrigger>
                    <SelectContent>
                      {riders.map(rider => (
                        <SelectItem key={rider.rider_id} value={rider.rider_id}>
                          {rider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Goal *</Label>
                  <Textarea 
                    value={editingGoal.goal_text} 
                    onChange={(e) => setEditingGoal({ ...editingGoal, goal_text: e.target.value })}
                    rows={3}
                    required
                    data-testid="edit-goal-text-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Target Date</Label>
                  <Input 
                    type="date" 
                    value={editingGoal.target_date || ''} 
                    onChange={(e) => setEditingGoal({ ...editingGoal, target_date: e.target.value })}
                    data-testid="edit-goal-target-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editingGoal.status} onValueChange={(v) => setEditingGoal({ ...editingGoal, status: v })}>
                    <SelectTrigger data-testid="edit-goal-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="achieved">Achieved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-forest hover:bg-forest-dark"
                  data-testid="submit-edit-goal-btn"
                >
                  Update Goal
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
